import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { bindFormModule } from '../../shared/formBinding.js';
import { formModalSaved, isFormModalOpen } from '../../shared/formModal.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// Creating and editing a lead, from the full page and from the shared dialog
// alike - bindFormModule handles both, so nothing here knows which it is on.

// Optional free-text fields. An empty one is sent as '' rather than dropped:
// dropping a key means "leave unchanged" on a PATCH, so clearing an email in the
// form would otherwise never reach the record.
//
// Every parsed field on a lead is in this list, which is the shape of the model
// rather than an oversight: the record exists to hold incomplete submissions, so
// any of them may legitimately be emptied.
const NULLABLE_TEXT = [
  'vorname',
  'nachname',
  'email',
  'telefon',
  'strasse',
  'plz',
  'ort',
  'beruf',
  'quelle',
  'quelleReferenz'
];

// Refs the form may clear. Sent as null rather than dropped, for the reason
// above - and `zugewiesenAn` especially: picking "Offen" is a deliberate choice
// that the lead goes back to being everybody's, not an absent answer.
const CLEARABLE_REFS = ['fachgebiet', 'zugewiesenAn', 'filiale'];

function readForm(form) {
  const data = Object.fromEntries(new FormData(form).entries());

  NULLABLE_TEXT.forEach((field) => {
    if (data[field] !== undefined) data[field] = data[field].trim();
  });

  CLEARABLE_REFS.forEach((field) => {
    if (data[field] === '') data[field] = null;
  });

  return data;
}

async function submit(form) {
  const submitButton = form.querySelector('button[type="submit"]');
  const { leadId } = form.dataset;
  const data = readForm(form);

  if (submitButton) submitButton.disabled = true;
  showLoading('Wird gespeichert …');

  try {
    const { data: body } = leadId
      ? await http.patch(`/leads/${leadId}`, data)
      : await http.post('/leads', data);

    // Editing in the dialog stays where it was: the page behind it - the inbox
    // or the lead's own page - is already the right place, so it closes and
    // refetches rather than navigating.
    if (leadId && isFormModalOpen()) {
      hideLoading();
      formModalSaved();
      notifySuccess('Lead wurde aktualisiert.');
      return;
    }

    // Creating goes to the new lead: it is the record somebody is about to keep
    // working on - attaching a CV, writing the first note - rather than a row to
    // admire in the list.
    const id = leadId || body.data.lead.id;
    window.location.href = withFlash(
      `/leads/${id}`,
      leadId ? 'lead-aktualisiert' : 'lead-erstellt'
    );
  } catch (err) {
    hideLoading();
    if (submitButton) submitButton.disabled = false;
    notifyError(
      err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
    );
  }
}

export function initLeadForm() {
  bindFormModule('lead-form', submit);
}
